import React, { Fragment, useEffect, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { LinkContainer } from 'react-router-bootstrap';
import moment from 'moment';
import { Button, Card, CardBody, Table, Input, Row, Col, FormGroup } from 'reactstrap';
import LoaderButton from '../../loader-button/LoaderButton';
import Switch from 'react-switch';
import FalconCardHeader from '../../common/FalconCardHeader';
import ButtonIcon from '../../common/ButtonIcon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import BZAPIService from '../../../services/BZAPIService';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import { VendorsContext } from '../../../context/VendorsContext';
import { DashboardContext } from '../../../context/DashboardContext';
import BulkUpdateWizard from '../BulkUpdateWizard';
import PlanProjectCell, { BugZeroPlanLabel, DeviceCountCell } from '../../bsa/inventory/PlanProjectCell';
import { usePlanProjectGate } from '../../bsa/inventory/usePlanProjectGate';
import { useDeviceCounts } from '../../bsa/inventory/useDeviceCounts';
import { productDisplayName } from '../../bz/productDisplayName';

/**
 * Managed Products tab content for use inside the DynamicConfigForm wizard.
 * Takes vendorId as a prop instead of from URL params.
 */
export default function ManagedProductsTab({ vendorId, cachedProducts, onProductsLoaded }) {
  const [managedProducts, setManagedProducts] = useState(cachedProducts || []);
  const [isLoading, setIsLoading] = useState(!cachedProducts);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const [showBulkUpdateWizard, setShowBulkUpdateWizard] = useState(false);

  // eslint-disable-next-line
  const [vendors] = useContext(VendorsContext);
  // eslint-disable-next-line
  const [dashboard] = useContext(DashboardContext);

  const vendor = vendors?.filter(v => v.id === vendorId)[0];

  const canCreatePlanProject = usePlanProjectGate(vendorId);
  const deviceCounts = useDeviceCounts(managedProducts, canCreatePlanProject);

  const isAnySelected = () => Object.values(selectedProducts).some(v => v === true);
  const getSelectedCount = () => Object.values(selectedProducts).filter(v => v === true).length;
  const getSelectedIds = () => Object.keys(selectedProducts).filter(id => selectedProducts[id] === true);

  useEffect(() => {
    // Skip fetch if we already have cached products
    if (cachedProducts) {
      setManagedProducts(cachedProducts);
      setIsLoading(false);
      return;
    }
    async function onLoad() {
      try {
        setManagedProducts([]);
        setIsLoading(true);
        const products = await BZAPIService.listManagedProducts(vendorId);
        setManagedProducts(products);
        onProductsLoaded?.(products);
        setIsLoading(false);
      } catch (e) {
        console.log(e);
        setIsLoading(false);
      }
    }
    onLoad();
    // eslint-disable-next-line
  }, [vendorId, cachedProducts]);

  async function updateProducts() {
    try {
      setIsUpdating(true);
      const products = await BZAPIService.listManagedProducts(vendorId);
      setManagedProducts(products);
      onProductsLoaded?.(products);
      setIsUpdating(false);
    } catch (e) {
      console.log(e);
      setIsUpdating(false);
    }
  }

  async function handleDelete(managedProduct) {
    if (!window.confirm(`Are you sure you want to delete ${managedProduct.name}?`)) return;
    try {
      await BZAPIService.deleteManagedProduct(managedProduct.id);
      const products = await BZAPIService.listManagedProducts(vendorId);
      setManagedProducts(products);
    } catch (e) {
      console.log(e);
      toast.error('Failed to delete product');
    }
  }

  async function handleToggleEnable(managedProduct) {
    if (
      !window.confirm(
        `Are you sure you want to ${managedProduct.isDisabled ? 'enable' : 'disable'} ${managedProduct.name}?`
      )
    )
      return;
    managedProduct.isDisabled = !managedProduct.isDisabled;
    try {
      await BZAPIService.createOrUpdateManagedProduct(managedProduct);
      const products = await BZAPIService.listManagedProducts(vendorId);
      setManagedProducts(products);
    } catch (e) {
      console.log(e);
      toast.error('Failed to update product');
    }
  }

  function handleSelectProduct(productId) {
    setSelectedProducts({ ...selectedProducts, [productId]: !selectedProducts[productId] });
  }

  function handleSelectAll() {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    const newSelected = {};
    managedProducts.forEach(product => {
      newSelected[product.id] = newSelectAll;
    });
    setSelectedProducts(newSelected);
  }

  function handleBulkUpdateSuccess() {
    setShowBulkUpdateWizard(false);
    setSelectedProducts({});
    setSelectAll(false);
    updateProducts();
  }

  if (isLoading)
    return (
      <Card>
        <CardBody>
          <div className="d-flex flex-column align-items-center justify-content-center py-5">
            <div className="spinner-border text-primary mb-3" role="status" style={{ width: '2rem', height: '2rem' }}>
              <span className="sr-only">Loading...</span>
            </div>
            <p className="text-muted mb-0">Loading managed products...</p>
          </div>
        </CardBody>
      </Card>
    );

  return (
    <Fragment>
      <Card>
        {vendorId === 'hpe' && (
          <FalconCardHeader
            title={
              <LinkContainer key="new" to={`/vendors/${vendorId}/products/new`}>
                <ButtonIcon color="falcon-default" size="sm" icon="plus" iconClassName="fs--2">
                  Add managed product
                </ButtonIcon>
              </LinkContainer>
            }
          />
        )}
        <CardBody className="fs--1">
          <Row className="mb-3">
            <Col className="text-left">
              <Button color="primary" disabled={!isAnySelected()} onClick={() => setShowBulkUpdateWizard(true)}>
                Bulk Update ({getSelectedCount()})
              </Button>
            </Col>
            <Col className="text-right">
              <LoaderButton
                className="btn btn-falcon-danger"
                onClick={updateProducts}
                name="save-and-add"
                isLoading={isUpdating}
                title="Update Products"
                aria-label="Update Products"
              >
                <FontAwesomeIcon icon="redo" />
              </LoaderButton>
            </Col>
          </Row>

          {managedProducts.length ? (
            <Table striped bordered hover style={{ marginTop: '20px' }}>
              <thead className="card-header">
                <tr>
                  <th style={{ width: '60px', textAlign: 'center', padding: '1em' }}>
                    <FormGroup check className="mb-0 d-flex justify-content-center">
                      <Input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                    </FormGroup>
                  </th>
                  <th>Name</th>
                  <th>Last Sync</th>
                  <th>Enabled</th>
                  <th>Devices</th>
                  <th>
                    <BugZeroPlanLabel />
                  </th>
                  {vendorId === 'hpe' && <th>Remove</th>}
                </tr>
              </thead>
              <tbody>
                {managedProducts.map(mp => (
                  <tr key={mp.id}>
                    <td style={{ textAlign: 'center', verticalAlign: 'middle', padding: '1em' }}>
                      <FormGroup check className="mb-0 d-flex justify-content-center">
                        <Input
                          type="checkbox"
                          checked={selectedProducts[mp.id] || false}
                          onChange={() => handleSelectProduct(mp.id)}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                      </FormGroup>
                    </td>
                    <td style={{ padding: 0 }}>
                      <Link
                        style={{ display: 'inline-block', width: '100%', height: '100%', padding: '1em' }}
                        to={`/vendors/${vendorId}/products/${mp.id}/${mp.name}`}
                      >
                        {productDisplayName(vendorId, mp.name)}
                      </Link>
                    </td>
                    <td>
                      {mp.lastExecution
                        ? moment
                            .utc(mp.lastExecution)
                            .local()
                            .fromNow()
                        : 'Pending...'}
                    </td>
                    <td>
                      <Switch onChange={() => handleToggleEnable(mp)} checked={!mp.isDisabled} onColor="#253746" />
                    </td>
                    <td>
                      <DeviceCountCell count={deviceCounts.get(mp.id)} canCreatePlanProject={canCreatePlanProject} />
                    </td>
                    <td>
                      <PlanProjectCell managedProduct={mp} canCreatePlanProject={canCreatePlanProject} />
                    </td>
                    {vendorId === 'hpe' && (
                      <td>
                        <Button color="falcon-danger" size="sm" onClick={() => handleDelete(mp)}>
                          <FontAwesomeIcon icon={faTimes} className="font-size-sm" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-4 text-muted">No managed products</div>
          )}
        </CardBody>
      </Card>

      <BulkUpdateWizard
        isOpen={showBulkUpdateWizard}
        toggle={() => setShowBulkUpdateWizard(false)}
        selectedProductIds={getSelectedIds()}
        selectedProducts={managedProducts.filter(p => selectedProducts[p.id])}
        vendorId={vendorId}
        vendor={vendor}
        onSuccess={handleBulkUpdateSuccess}
      />
    </Fragment>
  );
}

ManagedProductsTab.propTypes = {
  vendorId: PropTypes.string.isRequired,
  cachedProducts: PropTypes.array,
  onProductsLoaded: PropTypes.func
};
